/**
 * Shared helpers for calendar event formatting, time calculation, and display.
 */
import moment from 'moment-timezone';

// ---------------------------------------------------------------------------
// Time formatting
// ---------------------------------------------------------------------------

/** Format start+end ISO to "2:30 PM - 3:00 PM" in the given timezone. */
export function formatEventTime(
  start: string,
  end: string,
  timezone?: string
): string {
  const tz = timezone || moment.tz.guess();
  const s = moment.tz(start, tz);
  const e = moment.tz(end, tz);
  if (!s.isValid() || !e.isValid()) return '';
  // Booked events are NOT split at midnight (unlike busy events), so a
  // cross-day interview (e.g. 11:50 PM → 1:55 AM next day) would read
  // as a backwards same-day range. Same day → time-only; multi-day →
  // prefix each side with its date so the rollover is unambiguous in
  // the event pill + tooltip.
  if (s.isSame(e, 'day')) {
    return `${s.format('h:mm A')} - ${e.format('h:mm A')}`;
  }
  return `${s.format('MMM D, h:mm A')} - ${e.format('MMM D, h:mm A')}`;
}

/** Format a single ISO to "2:30 PM" in the given timezone. */
export function formatTime(iso: string, timezone?: string): string {
  const tz = timezone || moment.tz.guess();
  const m = moment.tz(iso, tz);
  return m.isValid() ? m.format('h:mm A') : '';
}

/**
 * Split an event that spans midnight into per-day chunks so each
 * visible segment carries its own start/end time. Without this
 * FullCalendar paints a single multi-day event whose label collapses to
 * "9:30 PM – 9:30 PM" on both days (the global start/end times read as
 * identical because only the hour-minute portion is shown).
 *
 * Same approach as legacy
 * `KInterviewScheduler/.../schProcessCalendarEvents.ts#processFullDayEvents`.
 *
 * Returns the original event in a 1-element array when it doesn't
 * cross a day boundary, so callers can `.flatMap(splitEventAcrossDays)`
 * unconditionally.
 */
export function splitEventAcrossDays<T extends { start: string; end: string }>(
  evt: T,
  timezone?: string
): T[] {
  const tz = timezone || moment.tz.guess();
  const s = moment.tz(evt.start, tz);
  const e = moment.tz(evt.end, tz);
  if (
    !s.isValid() ||
    !e.isValid() ||
    e.isSameOrBefore(s) ||
    s.isSame(e, 'day')
  ) {
    return [evt];
  }
  const chunks: T[] = [];
  let cursor = s.clone();
  while (cursor.isBefore(e)) {
    const dayEnd = cursor.clone().endOf('day');
    const chunkEnd = e.isBefore(dayEnd) ? e.clone() : dayEnd;
    chunks.push({
      ...evt,
      start: cursor.toISOString(),
      end: chunkEnd.toISOString(),
    });
    // Always advance to the start of the next day — using `chunkEnd`
    // would loop forever when the last chunk ends mid-day.
    cursor = dayEnd.clone().add(1, 'millisecond');
  }
  return chunks;
}

// ---------------------------------------------------------------------------
// Duration / time math
// ---------------------------------------------------------------------------

/** Calculate duration in minutes between two ISO strings. */
export function calcDurationMinutes(start: string, end: string): number {
  const s = moment(start);
  const e = moment(end);
  if (!s.isValid() || !e.isValid()) return 0;
  return Math.round(e.diff(s, 'minutes'));
}

/** Check if a datetime is in the past relative to the given timezone. */
export function isInPast(iso: string, timezone?: string): boolean {
  const tz = timezone || moment.tz.guess();
  return moment.tz(iso, tz).isBefore(moment().tz(tz));
}

/**
 * True when a slot's footprint START — the slot start minus its
 * before-buffer clearance — is already in the past. The `[before · event
 * · after]` block is validated as one unit; the before-buffer is its
 * earliest edge, so it slips into the past first as wall-clock time
 * advances. `beforeMinutes` should be the EFFECTIVE buffer (0 when buffer
 * is off), so with no buffer this is exactly `isInPast(start)`.
 */
export function isFootprintStartInPast(
  startIso: string,
  beforeMinutes: number,
  timezone?: string
): boolean {
  const tz = timezone || moment.tz.guess();
  return moment
    .tz(startIso, tz)
    .subtract(Math.max(0, beforeMinutes), 'minutes')
    .isBefore(moment().tz(tz));
}

/**
 * True when a slot's footprint START (start minus the before-buffer)
 * falls before the earliest bookable time — `now + minimumNoticeHours`.
 *
 * The minimum-notice rule rejects slots inside the `[now, now + notice)`
 * window — those are FUTURE, so a plain past-check (`isFootprintStartInPast`)
 * never flags them. As wall-clock time advances, a slot created at the
 * notice edge drifts into that window; this catches it before the save
 * round-trips a BE `err_slot_in_notice_time`. Mirrors the placement floor
 * (`now + notice + before`) and the BE `validate_time_slots_in_notice`.
 * With `noticeHours = 0` it reduces to `isFootprintStartInPast`.
 */
export function isFootprintBeforeEarliestBookable(
  startIso: string,
  beforeMinutes: number,
  noticeHours: number,
  timezone?: string
): boolean {
  const tz = timezone || moment.tz.guess();
  const earliest = moment().tz(tz).add(Math.max(0, noticeHours), 'hours');
  return moment
    .tz(startIso, tz)
    .subtract(Math.max(0, beforeMinutes), 'minutes')
    .isBefore(earliest);
}

/** Get the end ISO string from start + duration. */
export function addDuration(
  startIso: string,
  durationMinutes: number,
  timezone?: string
): string {
  const tz = timezone || moment.tz.guess();
  const m = moment.tz(startIso, tz);
  if (!m.isValid()) return '';
  return m.add(durationMinutes, 'minutes').toISOString();
}

// ---------------------------------------------------------------------------
// Event display helpers
// ---------------------------------------------------------------------------

/** Dynamic padding based on slot duration — tighter for short events. */
export function getEventPadding(durationMinutes: number): string {
  if (durationMinutes <= 15) return '0px 2px';
  if (durationMinutes <= 30) return '4px';
  return '6px';
}

/** Max duration in minutes (24 hours — matches form field cap). */
export const MAX_DURATION_MINUTES = 1440;

/** Minimum reachable duration via resize / drag-create (matches form field floor). */
export const MIN_DURATION_MINUTES = 5;

/**
 * Snap a duration to the manual-mode resize grid: uniform 5-min steps,
 * clamped to [MIN_DURATION_MINUTES, MAX_DURATION_MINUTES]. A dual grid
 * (5 below 15, 15 above) was tried first but felt glitchy — FC's mid-
 * drag preview snaps at 5 min while a post-release jump to 15-grid
 * produced visible value jumps near the boundary (e.g. dragging from
 * 30 → 25 visually jumped back to 30). Uniform 5-min keeps it smooth.
 */
export function snapManualDurationMinutes(minutes: number): number {
  if (!Number.isFinite(minutes) || minutes <= MIN_DURATION_MINUTES) {
    return MIN_DURATION_MINUTES;
  }
  if (minutes >= MAX_DURATION_MINUTES) {
    return MAX_DURATION_MINUTES;
  }
  return Math.round(minutes / 5) * 5;
}

/**
 * Boundary that manual click-to-create snaps the START time DOWN to.
 * Recruiters prefer slots starting on clean intervals, so a click at
 * 2:05 / 2:10 begins the slot at 2:00, and 2:20 / 2:25 at 2:15.
 * Tunable: 15 for quarter-hour, 30 for half-hour, etc. Click-only —
 * drag-create, move, resize, and Find Time never apply it.
 */
export const CLICK_START_SNAP_MINUTES = 15;

/**
 * Floor an ISO start time DOWN to the nearest `minutes` boundary, computed
 * in `timezone` so the boundary lands on the LOCAL clock (e.g. 2:00 / 2:30
 * as the recruiter sees it, not a UTC grid — matters for :30/:45-offset
 * zones like IST/Nepal). `minutes` is assumed to divide 60.
 */
export function snapStartDownToInterval(
  startIso: string,
  timezone: string,
  minutes: number = CLICK_START_SNAP_MINUTES
): string {
  const m = moment.tz(startIso, timezone);
  const floored = Math.floor(m.minutes() / minutes) * minutes;
  return m.startOf('hour').add(floored, 'minutes').toISOString();
}

/**
 * For a click on a busy-event harness, return the time range the harness
 * actually covers. FullCalendar renders one harness per day for a
 * multi-day event, but exposes `event.startStr` / `event.endStr` of the
 * full event (across all days). Mapping a click using the full event
 * range against the per-day harness rect produces wrong times — the
 * proportion-of-rect maps to a different proportion-of-event.
 *
 * Walks up to the day column (`[data-date]`), clamps the full event
 * range to that day's 00:00–24:00 window (matches our calendars'
 * `slotMinTime`/`slotMaxTime` config), and returns the chunk bounds.
 * Single-day events: clamp is a no-op, returns full bounds unchanged.
 */
export function busyEventChunkBounds(
  el: HTMLElement,
  fullStartMs: number,
  fullEndMs: number,
  timezone: string
): { chunkStartMs: number; chunkEndMs: number } {
  const dayCol = el.closest<HTMLElement>('[data-date]');
  const dateStr = dayCol?.getAttribute('data-date');
  if (!dateStr) return { chunkStartMs: fullStartMs, chunkEndMs: fullEndMs };
  const dayStart = moment.tz(dateStr, 'YYYY-MM-DD', timezone).valueOf();
  const dayEnd = moment
    .tz(dateStr, 'YYYY-MM-DD', timezone)
    .add(1, 'day')
    .valueOf();
  return {
    chunkStartMs: Math.max(fullStartMs, dayStart),
    chunkEndMs: Math.min(fullEndMs, dayEnd),
  };
}

/** Drag threshold in pixels to distinguish click from drag. */
export const DRAG_THRESHOLD_PX = 5;

/** Default slot duration in minutes (one grid cell). */
export const SLOT_DURATION_MINUTES = 15;

// ---------------------------------------------------------------------------
// Week range helpers
// ---------------------------------------------------------------------------

/** Get the week range key for a FullCalendar datesSet arg. */
export function getWeekRangeKey(startStr: string, endStr: string): string {
  return `${startStr}__${endStr}`;
}

/** Get a scroll-time string (HH:MM:SS) for a given hour, offset by -2. */
export function getScrollTime(hour: number): string {
  const scrollHour = Math.max(0, Math.min(22, hour - 2));
  return `${String(scrollHour).padStart(2, '0')}:00:00`;
}

/** Get the scroll time for "now" offset by -2 hours. */
export function getScrollTimeForNow(timezone?: string): string {
  const tz = timezone || moment.tz.guess();
  const hour = moment().tz(tz).hour();
  return getScrollTime(hour);
}
